import React, { useState, useRef, useEffect } from "react";
import { FileUp, Zap, X, FileText, BookOpen } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import Header from "./Header";
import UploadLoading from "./UploadLoading";
import { request } from "../service/HttpService";
import { errorMessage } from "../service/HttpErrors";

// Non-admin accounts may submit a limited number of papers per rolling 24 hours
// (backend SubmissionQuotaService); admins come back with limited=false.
const fetchQuota = () => request("GET", "/manuscript/quota");

function quotaText(quota) {
    if (!quota?.limited) return null;
    if (quota.remaining > 0) {
        return `You can submit ${quota.remaining} more paper${quota.remaining === 1 ? "" : "s"} right now (limit ${quota.limit} per 24 hours).`;
    }
    const when = quota.nextSlotAt
        ? ` You can submit another paper ${new Date(quota.nextSlotAt).toLocaleString([], { weekday: "short", hour: "numeric", minute: "2-digit" })}.`
        : "";
    return `You've reached the limit of ${quota.limit} papers per 24 hours.${when}`;
}

export default function Upload() {
    const navigate = useNavigate();
    const [runType, setRunType] = useState("quick");
    const [dragging, setDragging] = useState(false);
    const [file, setFile] = useState(null);
    const [title, setTitle] = useState("");
    const [authors, setAuthors] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [submittedEmail, setSubmittedEmail] = useState(null);
    const [submittedNotifications, setSubmittedNotifications] = useState(false);
    const [quota, setQuota] = useState(null);
    // The last submit was refused by the daily limit (its message shows even if
    // a refresh then finds a slot has opened)
    const [quotaRejected, setQuotaRejected] = useState(false);
    const fileInputRef = useRef(null);
    const apiDoneRef = useRef(null);   // null = pending, {email, notifications} = success, string = error msg
    const animDoneRef = useRef(false);
    const quotaExhausted = !!quota?.limited && quota.remaining <= 0;

    useEffect(() => {
        const refresh = () => fetchQuota().then(setQuota).catch(() => {});
        refresh();
        // A slot may have opened while the tab sat in the background
        window.addEventListener("focus", refresh);
        return () => window.removeEventListener("focus", refresh);
    }, []);

    // At the limit, re-check once the oldest submission ages out of the window.
    // Timed from the server's retryAfterSeconds, not nextSlotAt vs the local
    // clock, so a skewed clock can't turn this into a polling loop.
    const retryAfterSeconds = quotaExhausted ? quota.retryAfterSeconds : 0;
    const nextSlotAt = quota?.nextSlotAt;
    useEffect(() => {
        if (!retryAfterSeconds) return;
        const id = setTimeout(
            () => fetchQuota().then(setQuota).catch(() => {}),
            Math.max(5, retryAfterSeconds + 1) * 1000
        );
        return () => clearTimeout(id);
    }, [retryAfterSeconds, nextSlotAt]);

    const handleFile = (f) => {
        if (f && f.type === "application/pdf") {
            setFile(f);
            setError("");
            setQuotaRejected(false);
        } else {
            setError("Please select a PDF file.");
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        handleFile(e.dataTransfer.files[0]);
    };

    const finalize = (result) => {
        if (result === "sample") {
            navigate("/sample/submission");
        } else if (result === "quota") {
            // The quota message under the form explains the limit and when it resets
            setLoading(false);
            setQuotaRejected(true);
        } else if (typeof result === "object") {
            setLoading(false);
            setSubmittedEmail(result.email ?? null);
            setSubmittedNotifications(result.notifications ?? false);
            setSubmitted(true);
            fetchQuota().then(setQuota).catch(() => {});
        } else {
            setLoading(false);
            setError(result || "Upload failed. Please try again.");
        }
    };

    const handleAnimDone = () => {
        animDoneRef.current = true;
        if (apiDoneRef.current !== null) {
            finalize(apiDoneRef.current);
        }
    };

    const handleSubmit = async () => {
        if (runType !== "sample" && !file) { setError("Please select a PDF file."); return; }

        setLoading(true);
        setError("");
        setQuotaRejected(false);
        apiDoneRef.current = null;
        animDoneRef.current = false;

        if (runType === "sample") {
            apiDoneRef.current = "sample";
            if (animDoneRef.current) finalize("sample");
            return;
        }

        try {
            const { presignedUrl } = await request("GET", "/manuscript/presigned/single");
            await fetch(presignedUrl.url, { method: "PUT", body: file });
            const res = await request("POST", "/manuscript/insert/single", {
                key: presignedUrl.key,
                name: file.name,
                title: title.trim(),
                authors: authors.trim(),
            });
            apiDoneRef.current = { email: res.email, notifications: res.notifications };
        } catch (err) {
            if (err?.code === "DAILY_SUBMISSION_LIMIT" && err.body?.quota) {
                setQuota(err.body.quota);
                apiDoneRef.current = "quota";
            } else {
                apiDoneRef.current = errorMessage(err, "Upload failed. Please try again.");
            }
        }
        if (animDoneRef.current) {
            finalize(apiDoneRef.current);
        }
    };

    return (
        <div className="home-layout">
            <Sidebar activePage="Upload Paper" />

            <main className="home-main">
                <Header />

                <div className="home-content">
                        <div className="upload-main">
                            {loading ? (
                                <UploadLoading onDone={handleAnimDone} />
                            ) : submitted ? (
                                <div className="upload-success-wrap">
                                    <div className="upload-success-icon">✓</div>
                                    <h2 className="upload-success-title">Paper received</h2>
                                    <p className="upload-success-msg">
                                        Looks like we have quite a few papers waiting to score! We have received your paper and will process it shortly.{" "}
                                        {submittedNotifications && submittedEmail ? (
                                            <>We will send an email to <strong>{submittedEmail}</strong> when processing is complete. Make sure to check your spam.</>
                                        ) : (
                                            <>Check the <button className="upload-success-link" onClick={() => navigate("/submissions")}>submissions page</button> for the status of your paper.</>
                                        )}
                                    </p>
                                    <div className="upload-success-actions">
                                        <button className="start-analysis-btn" onClick={() => { setSubmitted(false); setFile(null); setTitle(""); setAuthors(""); setError(""); }}>
                                            Upload Another
                                        </button>
                                        <button className="sub-action-btn sub-action-outline" onClick={() => navigate("/submissions")}>
                                            View Submissions
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="upload-page-title-row">
                                        <div>
                                            <h1 className="upload-page-heading">Upload Research Paper</h1>
                                            <p className="upload-page-meta">Submit your manuscript for analysis</p>
                                            {quota?.limited && !quotaExhausted && (
                                                <p className="upload-page-meta">{quotaText(quota)}</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Drop zone */}
                                    {file ? (
                                        <div className="upload-file-chosen">
                                            <FileText size={18} className="upload-file-icon" />
                                            <span className="upload-file-name">{file.name}</span>
                                            <button className="upload-file-remove" onClick={() => setFile(null)}>
                                                <X size={13} />
                                            </button>
                                        </div>
                                    ) : (
                                        <div
                                            className={`upload-dropzone${dragging ? " upload-dropzone-active" : ""}`}
                                            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                                            onDragLeave={() => setDragging(false)}
                                            onDrop={handleDrop}
                                            onClick={() => fileInputRef.current.click()}
                                        >
                                            <p className="upload-dropzone-text">Drag & drop your manuscript</p>
                                            <button className="select-pdf-btn" onClick={(e) => { e.stopPropagation(); fileInputRef.current.click(); }}>
                                                <FileUp size={14} />
                                                Select PDF
                                            </button>
                                        </div>
                                    )}
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="application/pdf"
                                        style={{ display: "none" }}
                                        onChange={(e) => handleFile(e.target.files[0])}
                                    />

                                    {/* Form */}
                                    <div className="upload-form">
                                        <div className="upload-field">
                                            <label className="upload-label">Title</label>
                                            <input className="upload-input" value={title} onChange={(e) => setTitle(e.target.value)} />
                                        </div>
                                        <div className="upload-field">
                                            <label className="upload-label">Authors</label>
                                            <input className="upload-input" value={authors} onChange={(e) => setAuthors(e.target.value)} />
                                        </div>
                                    </div>

                                    {/* Run Type */}
                                    <div className="upload-run-type">
                                        <label className="upload-label">Run Type</label>
                                        <div className="run-type-options">
                                            <div
                                                className={`run-type-card${runType === "quick" ? " run-type-active" : ""}`}
                                                onClick={() => setRunType("quick")}
                                            >
                                                <div className="run-type-header">
                                                    <Zap size={14} />
                                                    <span className="run-type-name">Quick Analysis</span>
                                                </div>
                                                <p className="run-type-desc">Rapid summary assessment</p>
                                            </div>
                                            <div
                                                className={`run-type-card${runType === "sample" ? " run-type-active" : ""}`}
                                                onClick={() => setRunType("sample")}
                                            >
                                                <div className="run-type-header">
                                                    <BookOpen size={14} />
                                                    <span className="run-type-name">Sample Report</span>
                                                </div>
                                                <p className="run-type-desc">Preview the report experience</p>
                                            </div>
                                        </div>
                                    </div>

                                    {error && <p className="chpw-error">{error}</p>}
                                    {quotaExhausted && runType !== "sample" && (
                                        <p className="chpw-error">{quotaText(quota)}</p>
                                    )}
                                    {quotaRejected && !quotaExhausted && (
                                        <p className="chpw-error">Your paper wasn't submitted because you'd reached the daily limit. A slot has opened since, so you can try again now.</p>
                                    )}

                                    <p className="beta-disclaimer">This product is in beta. Scores and analysis are experimental and may not fully reflect the quality of your work.</p>

                                    <button className="start-analysis-btn" onClick={handleSubmit} disabled={loading || (quotaExhausted && runType !== "sample")}>
                                        Start Analysis
                                    </button>
                                </>
                            )}
                        </div>
                </div>
            </main>
        </div>
    );
}
